// eslint-disable-next-line react-refresh/only-export-components
export { sourceBg, MarkdownText } from "./MarkdownText";
export type { MarkdownTextProps } from "./MarkdownText";

export { AllowlistDropdown, ToolApprovalCard } from "./ToolApprovalCard";
export type { ToolApprovalCardProps } from "./ToolApprovalCard";

export { ToolDraftCard, EmailDraftCard } from "./DraftCards";
export type { ToolDraftCardProps, EmailDraftCardProps, EmailPreviousMessage } from "./DraftCards";

export { AgentMessageList } from "./MessageList";
export type { AgentMessageListProps } from "./MessageList";

export { DecisionSummary } from "./DecisionSummary";
export type { DecisionSummaryProps } from "./DecisionSummary";

export { PendingPromptPanel } from "./PendingPromptPanel";
export type { PendingPromptPanelProps } from "./PendingPromptPanel";

export {
  AgentLandingComposer,
  AgentDockedComposer,
  ReplyToBar,
} from "./ComposerPanels";
export type { ModelSelectorItem, ComposerSharedProps } from "./ComposerPanels";

import type { JSX } from "react";
import type { IconName } from "../ui/Icon";
import { IconRoundBadge } from "../ui/PanelPrimitives";
import type { AppRuntime } from "../../runtime/contracts/runtime";
import { EntityCardRenderer } from "../shared/EntityCardRenderer";

export interface AgentPanelHeaderTitleProps {
  readonly title: string;
  readonly sourceIconName?: IconName;
  readonly sourceColorClassName?: string;
}

export function AgentPanelHeaderTitle({
  title,
  sourceIconName,
  sourceColorClassName,
}: AgentPanelHeaderTitleProps): JSX.Element {
  if (!sourceIconName) return <>{title}</>;
  return (
    <span className="inline-flex items-center gap-2">
      <IconRoundBadge
        iconName={sourceIconName}
        colorClassName={sourceColorClassName ?? "bg-accent"}
        iconSize={14}
        className="h-8 w-8"
      />
      <span className="truncate">{title}</span>
    </span>
  );
}

const MAX_VISIBLE_ENTITIES = 5;

// Entity extraction moved to @magnis/client-core (WS-1 consolidation, A-2)
// so the CLI shares it. Re-exported here because this file is the
// established import path for the web AND the plugin host shim
// (@magnis/host/agent → hostShims/agent.ts → this file).
import { extractEntities } from "@magnis/client-core";

// eslint-disable-next-line react-refresh/only-export-components
export { extractEntities };
export type { ExtractEntitiesOptions } from "@magnis/client-core";

export interface ToolResultEntityCardsProps {
  readonly toolName: string;
  readonly result: unknown;
  readonly runtime: AppRuntime;
}

export function ToolResultEntityCards({
  toolName: _toolName,
  result,
  runtime,
}: ToolResultEntityCardsProps): JSX.Element {
  const entities = extractEntities(result);
  const total =
    result && typeof result === "object" && "total" in (result as Record<string, unknown>)
      ? (result as Record<string, unknown>).total as number
      : entities.length;
  const visible = entities.slice(0, MAX_VISIBLE_ENTITIES);
  const remaining = total - visible.length;

  return (
    <div className="space-y-1">
      <div className="space-y-1">
        {visible.map((entity, i) => {
          const schemaId = entity.schema_id as string;
          // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
          const key = (entity.id as string | undefined) ?? `entity-${i}`;
          return (
            <EntityCardRenderer
              key={key}
              schemaId={schemaId}
              data={entity}
              runtime={runtime}
            />
          );
        })}
      </div>
      {remaining > 0 && (
        <div className="px-1 text-[11px] text-agent-text-dim">
          +{remaining} more
        </div>
      )}
    </div>
  );
}

// Re-export tool classification for use by AgentPanel
